/**
 * +prof: gathering at herb and ore nodes on open maps (docs/design/professions.md).
 *
 *   - interact `what: 'gather:<nodeId>'`: a living hero, out of combat, within GATHER_REACH of a
 *     ready node whose `req` is at most its skill in that profession (setGatherSkill; default 1)
 *     starts a channel of the node's `channelMs` ('gather' start). It stops walking and faces the node.
 *     Refusals emit 'gather' refused with a reason (the HUD says why).
 *   - the channel is cancelled when the hero moves (or jumps), takes damage, enters combat, starts a
 *     cast or dies ('gather' cancel + reason);
 *   - when it ends the node is depleted ('gather' done) and comes back after a seeded delay in its
 *     `respawnMs` range.
 *
 * The sim rolls no loot: progression's `applyGather` pays the gather (server-side online) from the
 * 'done' event. Determinism: respawn delays use their own stream keyed by (world seed, node id,
 * gather count), never the world RNG, so combat is bit-identical with or without gathering.
 */
import type { Command, FieldDef, GatherEvent, GatherNodeDef, GatherProfession, GatherState, SimContext, Unit, UnitId, World } from '../contracts';
import { GATHER_REACH } from '../contracts';
import { createSeededRng } from '../core/rng';
import { objectSeed } from '../worldloot/index';

/** The channel breaks when the hero drifts this far from where it started (yards). */
const MOVE_EPS = 0.25;

const fieldOf = (ctx: SimContext): FieldDef | undefined => ctx.content.dungeon(ctx.world.dungeonId)?.field;

function stateOf(world: World): GatherState | null {
  const f = world.field;
  if (!f) return null;
  return (f.gather ??= { nodes: {}, channels: {}, skill: {} });
}

/** Is node `id` ready (not depleted) at `now`? */
/** Structural view of what the readers need (works with a DeepReadonly world). */
type GatherReadable = { readonly time: number; readonly field?: { readonly gather?: { readonly nodes: { readonly [id: string]: { readonly readyAt: number } }; readonly skill: { readonly [unit: string]: { readonly foraging?: number; readonly mining?: number } } } } };

export function gatherNodeReady(world: GatherReadable, id: string, now = world.time): boolean {
  const n = world.field?.gather?.nodes[id];
  return !n || now >= n.readyAt;
}

/** A hero's skill in `prof` as the sim knows it (setGatherSkill; default 1). */
export function gatherSkillOf(world: Pick<GatherReadable, 'field'>, unitId: UnitId, prof: GatherProfession): number {
  return world.field?.gather?.skill[unitId]?.[prof] ?? 1;
}

/** Why `unitId` can't gather `node` right now (null = it can). */
export function gatherBlocker(world: Readonly<World>, unitId: UnitId, node: Readonly<GatherNodeDef>): GatherEvent['reason'] | null {
  const u = world.units[unitId];
  if (!u || !u.alive || u.departed) return 'dead';
  if (gatherSkillOf(world, unitId, node.profession) < node.req) return 'skill';
  if (!gatherNodeReady(world, node.id)) return 'depleted';
  if (Math.hypot(u.pos.x - node.pos.x, u.pos.y - node.pos.y) > GATHER_REACH) return 'far';
  if (u.inCombat) return 'combat';
  if (world.field?.gather?.channels[unitId]) return 'busy';
  return null;
}

/** Command 'setGatherSkill'. */
export function setGatherSkill(ctx: SimContext, cmd: Extract<Command, { type: 'setGatherSkill' }>): void {
  const st = stateOf(ctx.world);
  const ok = (v: number): number => (Number.isFinite(v) ? Math.max(0, Math.min(1000, Math.floor(v))) : 1);
  if (st) st.skill[cmd.actorId] = { foraging: ok(cmd.foraging), mining: ok(cmd.mining) };
}

const emit = (ctx: SimContext, unitId: UnitId, node: Readonly<GatherNodeDef>, state: GatherEvent['state'], extra: Partial<GatherEvent> = {}): void =>
  ctx.emit({ type: 'gather', sourceID: unitId, nodeId: node.id, kind: node.kind, profession: node.profession, tier: node.tier, state, ...extra });

/** Interact 'gather:<id>': start a channel. Returns whether it started. */
export function startGather(ctx: SimContext, unitId: UnitId, nodeId: string): boolean {
  const w = ctx.world;
  const field = fieldOf(ctx);
  const node = field?.gatherNodes?.find((n) => n.id === nodeId);
  const st = stateOf(w);
  const u = w.units[unitId];
  if (!node || !st || !u || u.kind !== 'player') return false;
  const why = gatherBlocker(w, unitId, node);
  if (why) {
    emit(ctx, unitId, node, 'refused', { reason: why });
    return false;
  }
  if (u.cast) ctx.sys.casting.cancelCast(ctx, u.id, 'player');
  u.queued = null;
  u.move = null;
  u.facing = Math.atan2(node.pos.y - u.pos.y, node.pos.x - u.pos.x);
  const ms = gatherChannelMs(ctx, u, node.channelMs); // +grid: gatherSpeedPct
  st.channels[unitId] = { nodeId, startAt: ctx.now, endAt: ctx.now + ms, pos: { x: u.pos.x, y: u.pos.y }, hp: u.hp };
  emit(ctx, unitId, node, 'start', { durationMs: ms });
  return true;
}

/** +grid: a gather channel's length for this hero: base ÷ (1 + Σ gatherSpeedPct) (Waystone Grid utility stars). */
export function gatherChannelMs(ctx: Pick<SimContext, 'content'>, u: Unit, baseMs: number): number {
  let v = 0;
  for (const inst of u.auras) for (const m of ctx.content.aura(inst.auraId)?.mods ?? []) if (m.stat === 'gatherSpeedPct') v += m.value * (m.ignoreStacks ? 1 : inst.stacks);
  return v > 0 ? Math.max(1, Math.round(baseMs / (1 + v))) : baseMs;
}

/** Per tick (field/index.ts): interrupts and completions. */
export function tickGather(ctx: SimContext): void {
  const w = ctx.world;
  const st = w.field?.gather;
  if (!st) return;
  const ids = Object.keys(st.channels);
  if (!ids.length) return;
  const field = fieldOf(ctx);
  for (const id of ids.sort()) {
    const ch = st.channels[id]!;
    const node = field?.gatherNodes?.find((n) => n.id === ch.nodeId);
    const u = w.units[id];
    if (!node) {
      delete st.channels[id];
      continue;
    }
    let why: GatherEvent['reason'] | null = null;
    if (!u || !u.alive || u.departed) why = 'dead';
    else if (u.hp < ch.hp - 0.5) why = 'damage';
    else if (u.inCombat) why = 'combat';
    else if (u.cast) why = 'cast';
    else if (u.jump || u.move || Math.hypot(u.pos.x - ch.pos.x, u.pos.y - ch.pos.y) > MOVE_EPS) why = 'moved';
    if (why) {
      delete st.channels[id];
      emit(ctx, id, node, 'cancel', { reason: why });
      continue;
    }
    if (ctx.now < ch.endAt) continue;
    delete st.channels[id];
    const prev = st.nodes[node.id];
    const gathers = (prev?.gathers ?? 0) + 1;
    const [lo, hi] = node.respawnMs;
    const rng = createSeededRng(objectSeed(w.seed, node.id, 0x6a7 + gathers));
    const delay = hi > lo ? lo + Math.floor(rng.next() * (hi - lo + 1)) : lo;
    st.nodes[node.id] = { readyAt: ctx.now + delay, gathers };
    emit(ctx, id, node, 'done', { durationMs: ch.endAt - ch.startAt });
  }
}

/** Nodes a hero could gather from where it stands (ready, skill ok, in reach), nearest first. */
export function gatherableNodes(world: Readonly<World>, field: Readonly<Pick<FieldDef, 'gatherNodes'>> | undefined, unitId: UnitId): GatherNodeDef[] {
  const u = world.units[unitId];
  if (!u) return [];
  return (field?.gatherNodes ?? [])
    .filter((n) => gatherBlocker(world, unitId, n) === null)
    .sort((a, b) => Math.hypot(a.pos.x - u.pos.x, a.pos.y - u.pos.y) - Math.hypot(b.pos.x - u.pos.x, b.pos.y - u.pos.y));
}
